import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface EmailRequest {
  to: string[];
  subject: string;
  html: string;
  templateId?: string;
  sequenceId?: string;
  metadata?: Record<string, unknown>;
}

export const useSalesBlink = () => {
  const { toast } = useToast();

  const sendEmail = useCallback(async (emailRequest: EmailRequest) => {
    try {
      const { data, error } = await supabase.functions.invoke('salesblink-service', {
        body: { action: 'SEND_EMAIL', ...emailRequest }
      });

      if (error) throw error;

      toast({
        title: 'Email Sent',
        description: 'The email was sent successfully'
      });

      return data;
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to send email'
      });
      throw error;
    }
  }, [toast]);

  const startSequence = useCallback(async (sequenceData: any) => {
    try {
      const { data, error } = await supabase.functions.invoke('salesblink-service', {
        body: { action: 'START_SEQUENCE', ...sequenceData }
      });

      if (error) throw error;

      toast({
        title: 'Sequence Started',
        description: 'The sequence was started successfully'
      });

      return data;
    } catch (error) {
      console.error('Error starting sequence:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to start sequence'
      });
      throw error;
    }
  }, [toast]);

  const enrichLead = useCallback(async (leadData: any) => {
    try {
      const { data, error } = await supabase.functions.invoke('salesblink-service', {
        body: { action: 'ENRICH_LEAD', ...leadData }
      });

      if (error) throw error;

      toast({
        title: 'Lead Enriched',
        description: 'The lead was enriched successfully'
      });

      return data;
    } catch (error) {
      console.error('Error enriching lead:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to enrich lead'
      });
      throw error;
    }
  }, [toast]);

  return {
    sendEmail,
    startSequence,
    enrichLead
  };
};